<template>
    <v-container fluid class="awards-container pa-2 pa-md-4">
        <v-row>
            <v-col cols="12">
                <v-row>
                    <v-col cols="12" md="4" lg="3">
                        <v-select
                            v-model="selectedSessionId"
                            :items="sessions"
                            :loading="loadingSessions"
                            label="Select Session"
                            outlined
                            hide-details
                            class="session-select mb-4"
                            item-text="title"
                            item-value="id"
                        />
                    </v-col>
                    <v-col cols="12" md="4" lg="5">
                        <v-tooltip bottom max-width="300">
                            <template #activator="{ on, attrs }">
                                <div v-bind="attrs" class="d-flex align-center" v-on="on">
                                    <v-checkbox
                                        v-if="selectedSessionId"
                                        v-model="sessionHasBeenAwarded"
                                        color="success"
                                        label="Entire Session Has Been Awarded"
                                        hide-details
                                        class="session-checkbox mt-0"
                                        @change="markSessionAwarded"
                                    />
                                </div>
                            </template>
                            <span>
                                Marks the entire session as having completed its awards ceremony. When checked, it will
                                automatically mark all divisions as awarded. Use this as a final confirmation when all
                                awards have been distributed.
                            </span>
                        </v-tooltip>
                    </v-col>
                    <v-col cols="12" md="4" lg="4" class="d-flex justify-end align-center">
                        <v-progress-circular v-if="saving" indeterminate color="primary" size="32" class="mt-2" />
                    </v-col>
                </v-row>

                <v-row v-if="selectedSessionId && resultCategories.length > 0">
                    <v-col cols="12">
                        <v-card class="mb-4 ceremony-progress-card" outlined>
                            <v-card-text class="pb-0">
                                <div class="d-flex align-center mb-2">
                                    <v-icon :color="progressColor" class="mr-2">{{ progressIcon }}</v-icon>
                                    <div class="text-h6">Awards Ceremony Progress</div>
                                    <v-spacer></v-spacer>
                                    <div class="text-h6 font-weight-bold" :class="progressColor + '--text'">
                                        {{ progressPercentage }}%
                                    </div>
                                </div>
                                <div class="text-subtitle-2 grey--text mb-2">
                                    {{ completedDivisions }} of {{ resultCategories.length }} divisions completed
                                </div>
                                <v-progress-linear
                                    :value="progressPercentage"
                                    height="12"
                                    :color="progressColor"
                                    rounded
                                    striped
                                    class="mb-3"
                                >
                                    <template #default="{ value }">
                                        <strong>{{ Math.ceil(value) }}%</strong>
                                    </template>
                                </v-progress-linear>

                                <div class="ceremony-stats d-flex flex-wrap justify-space-between mb-3">
                                    <v-chip class="ma-1" color="primary" outlined>
                                        <v-icon left small>mdi-account-group</v-icon>
                                        {{ totalAthletes }} Athletes
                                    </v-chip>
                                    <v-chip class="ma-1" color="success" outlined>
                                        <v-icon left small>mdi-check-circle</v-icon>
                                        {{ checkedInAthletes }} Checked In
                                    </v-chip>
                                    <v-chip class="ma-1" color="indigo" outlined>
                                        <v-icon left small>mdi-medal</v-icon>
                                        {{ totalAwards }} Awards
                                    </v-chip>
                                    <v-chip class="ma-1" color="deep-purple" outlined>
                                        <v-icon left small>mdi-trophy</v-icon>
                                        {{ completedDivisions }} Divisions Complete
                                    </v-chip>
                                </div>

                                <v-expand-transition>
                                    <div
                                        v-if="progressPercentage === 100"
                                        class="celebration-container text-center py-3"
                                    >
                                        <v-icon color="amber darken-2" x-large class="celebration-icon mb-2"
                                            >mdi-trophy</v-icon
                                        >
                                        <h3 class="text-h5 success--text">Awards Ceremony Complete!</h3>
                                        <v-btn color="success" class="mt-2" @click="showConfetti">
                                            <v-icon left>mdi-party-popper</v-icon>
                                            Celebrate
                                        </v-btn>
                                    </div>
                                </v-expand-transition>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col>
                        <v-tabs v-if="selectedSessionId" v-model="tab" class="mt-4 awards-tabs" show-arrows>
                            <v-tab href="#tab-check-in" class="px-6 py-4">
                                <v-icon left size="24">mdi-account-check</v-icon>
                                Check-in
                            </v-tab>
                            <v-tab href="#tab-awards" class="px-6 py-4">
                                <v-icon left size="24">mdi-medal</v-icon>
                                Awards
                            </v-tab>
                            <v-tab href="#tab-settings" class="px-6 py-4">
                                <v-icon left size="24">mdi-cog</v-icon>
                                Settings
                            </v-tab>
                        </v-tabs>

                        <v-tabs-items v-if="selectedSessionId" v-model="tab" class="awards-tabs-content">
                            <v-tab-item value="tab-check-in">
                                <AwardAthletes
                                    :result-categories="resultCategories"
                                    @saving="saving = $event"
                                    @skip-checkin="tab = 'tab-awards'"
                                />
                            </v-tab-item>

                            <v-tab-item value="tab-awards">
                                <v-expansion-panels>
                                    <v-expansion-panel
                                        v-for="resultCategory in sortedResultCategories"
                                        :key="`result-category-${resultCategory.id}`"
                                        :class="{ 'completed-panel': resultCategory.awarded_at }"
                                    >
                                        <AwardResultCategory
                                            :result-category="resultCategory"
                                            @saving="saving = $event"
                                        />
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </v-tab-item>

                            <v-tab-item value="tab-settings">
                                <AwardSettings
                                    :meet-id="meetId"
                                    :categories="resultCategories"
                                    @saving="saving = $event"
                                />
                            </v-tab-item>
                        </v-tabs-items>

                        <v-container v-else class="text-center py-8">
                            <v-icon size="48" color="grey lighten-1" class="mb-4">mdi-playlist-edit</v-icon>
                            <div class="text-h6 grey--text">Select a session to view athletes.</div>
                        </v-container>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>

        <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" bottom>
            {{ snackbar.text }}
            <template #action="{ attrs }">
                <v-btn text v-bind="attrs" class="pa-3" @click="snackbar.show = false">
                    <v-icon left>mdi-close</v-icon>
                    Close
                </v-btn>
            </template>
        </v-snackbar>

        <!-- Confetti animation container -->
        <div ref="confettiContainer" class="confetti-container"></div>

        <!-- Progress summary bar at top -->
        <v-card outlined class="mb-4">
            <v-card-text class="progress-summary">
                <div class="d-flex flex-wrap align-center justify-space-between mb-2">
                    <span class="text-subtitle-1">Awards Progress</span>
                    <span class="text-subtitle-2"
                        >{{ awardStats.completedDivisions }} of {{ awardStats.totalDivisions }} divisions</span
                    >
                </div>
                <v-progress-linear
                    :value="awardStats.percentComplete"
                    height="8"
                    color="primary"
                    background-opacity="0.2"
                    rounded
                ></v-progress-linear>

                <!-- Simplify the progress stats (fewer icons and indicators) -->
                <div class="d-flex flex-wrap justify-space-around mt-3">
                    <div class="d-flex flex-column align-center ma-1 pa-1">
                        <span class="subtitle-2">Athletes</span>
                        <div class="d-flex align-center">
                            <span class="body-1"
                                >{{ awardStats.checkedInAthletes }} / {{ awardStats.totalAthletes }}</span
                            >
                        </div>
                    </div>

                    <div class="d-flex flex-column align-center ma-1 pa-1">
                        <span class="subtitle-2">Divisions</span>
                        <div class="d-flex align-center">
                            <span class="body-1"
                                >{{ awardStats.completedDivisions }} / {{ awardStats.totalDivisions }}</span
                            >
                        </div>
                    </div>

                    <div class="d-flex flex-column align-center ma-1 pa-1">
                        <span class="subtitle-2">Award Ceremony</span>
                        <div class="d-flex align-center">
                            <span class="body-1">{{ awardStats.percentComplete }}%</span>
                        </div>
                    </div>
                </div>
            </v-card-text>
        </v-card>

        <!-- Only show celebration when complete -->
        <v-slide-y-transition>
            <v-card v-if="awardStats.isComplete && !awardStats.isEmpty" class="celebration-card mb-4 text-center py-4">
                <v-icon color="amber darken-2" size="36">mdi-trophy</v-icon>
                <div class="text-h6 success--text my-2">All Awards Complete!</div>
                <!-- Remove extra animations and icons here -->
            </v-card>
        </v-slide-y-transition>
    </v-container>
</template>

<script>
import Session from '@/Models/Session.js';
import ResultCategory from '@/Models/ResultCategory.js';
import QueryBuilder from '@/query-builder.js';
import AwardResultCategory from '@/Components/Awards/AwardResultCategory.vue';
import AwardAthletes from '@/Components/Awards/AwardAthletes.vue';
import AwardSettings from '@/Components/Awards/AwardSettings.vue';

export default {
    name: 'Awards',
    components: { AwardAthletes, AwardResultCategory, AwardSettings },

    props: {
        meetId: {
            type: Number,
            required: true,
        },
    },

    data: () => ({
        tab: 'tab-check-in',
        loadingSessions: false,
        selectedSessionId: null,
        resultCategoryIds: [],
        sessionHasBeenAwarded: false,
        saving: false,
        snackbar: {
            show: false,
            color: '',
            text: '',
        },
        awardSettings: {
            awardRule: 'top3',
            customAwardCount: 3,
        },
        categoryOverrides: {},
        loadingSettings: false,
    }),

    computed: {
        selectedSession() {
            return this.sessions.find((session) => session.id === this.selectedSessionId);
        },

        sessions() {
            return Session.query().where('meet_id', this.meetId).get();
        },

        resultCategories() {
            return ResultCategory.query()
                .with('results', (query) => {
                    query
                        .orderBy('final_two_rank')
                        .orderBy('final_one_rank')
                        .orderBy('finals_rank')
                        .orderBy('prelims_rank');
                })
                .with('results.athlete')
                .whereIdIn(this.resultCategoryIds)
                .orderBy('event')
                .get();
        },

        sortedResultCategories() {
            // For now, use default ordering by event
            // This will be enhanced in Task 24 (Division Ordering System)
            return this.resultCategories;
        },

        // Progress calculation
        completedDivisions() {
            return this.resultCategories.filter((category) => category.awarded_at).length;
        },

        progressPercentage() {
            if (this.resultCategories.length === 0) return 0;
            return Math.round((this.completedDivisions / this.resultCategories.length) * 100);
        },

        progressColor() {
            if (this.progressPercentage === 100) return 'success';
            if (this.progressPercentage >= 75) return 'light-green';
            if (this.progressPercentage >= 50) return 'amber';
            if (this.progressPercentage >= 25) return 'orange';
            return 'blue-grey';
        },

        progressIcon() {
            if (this.progressPercentage === 100) return 'mdi-check-circle';
            if (this.progressPercentage >= 75) return 'mdi-crown';
            if (this.progressPercentage >= 50) return 'mdi-flag-checkered';
            if (this.progressPercentage >= 25) return 'mdi-run-fast';
            return 'mdi-flag-outline';
        },

        // Statistics for the ceremony
        totalAthletes() {
            return this.resultCategories.reduce((total, category) => total + category.results.length, 0);
        },

        checkedInAthletes() {
            return this.resultCategories.reduce(
                (total, category) => total + category.results.filter((result) => result.award_prepared_at).length,
                0,
            );
        },

        totalAwards() {
            // This is an approximation as it depends on award settings
            // We'll use a simplified calculation here
            return this.resultCategories.reduce(
                (total, category) => total + Math.min(3, category.results.length), // Assuming top 3 awards
                0,
            );
        },

        awardStats() {
            return {
                completedDivisions: this.completedDivisions,
                totalDivisions: this.resultCategories.length,
                percentComplete: this.progressPercentage,
                checkedInAthletes: this.checkedInAthletes,
                totalAthletes: this.totalAthletes,
                isComplete: this.progressPercentage === 100,
                isEmpty: this.resultCategories.length === 0,
            };
        },
    },

    watch: {
        selectedSessionId() {
            this.fetchAwardStatus();

            this.sessionHasBeenAwarded = !!this.selectedSession?.awarded_at;
        },

        async hasBeenAwarded(hasBeenAwarded) {
            if (hasBeenAwarded) {
                await this.markSessionAwarded();

                this.resultCategories.forEach((resultCategory) => {
                    this.markResultCategoryAwarded(resultCategory);
                });
            }
        },
    },

    created() {
        this.loadSessions();
        this.loadAwardSettings();
    },

    methods: {
        async loadSessions() {
            this.loadingSessions = true;

            const query = new QueryBuilder('sessions');
            Session.api().get(query.filter('meet_id', this.meetId).toUrl());

            this.loadingSessions = false;
        },

        async fetchAwardStatus() {
            this.resultCategoryIds = [];

            const { response } = await Session.api().get(
                `/api/awards?meet_id=${this.meetId}&session_id=${this.selectedSessionId}`,
            );

            response.data.data.flights.forEach((flight) => {
                flight.result_categories.forEach((resultCategory) => {
                    this.resultCategoryIds.push(resultCategory.id);
                });
            });
        },

        async markSessionAwarded() {
            this.saving = true;

            await Session.api().post('/api/awards', {
                meet_id: this.meetId,
                model_type: 'App\\Models\\Session',
                model_ids: [this.selectedSessionId],
                field: 'awarded',
                value: this.sessionHasBeenAwarded,
            });

            // Show success message
            this.snackbar = {
                show: true,
                color: 'success',
                text: this.sessionHasBeenAwarded
                    ? `Session "${this.selectedSession?.title}" marked as awarded`
                    : `Session "${this.selectedSession?.title}" marked as not awarded`,
            };

            this.saving = false;
        },

        async markResultCategoryAwarded(resultCategory) {
            this.saving = true;

            await ResultCategory.api().post('/api/awards', {
                meet_id: this.meetId,
                model_type: 'App\\Models\\ResultCategory',
                model_ids: [resultCategory.id],
                field: 'awarded',
                value: !resultCategory.awarded_at,
            });

            // Update local state
            resultCategory.awarded_at = resultCategory.awarded_at ? null : new Date();

            // Show feedback message
            this.snackbar = {
                show: true,
                color: 'success',
                text: resultCategory.awarded_at
                    ? `${resultCategory.description()} marked as awarded`
                    : `${resultCategory.description()} marked as not awarded`,
            };

            this.saving = false;
        },

        async loadAwardSettings() {
            this.loadingSettings = true;

            try {
                const { data } = await axios.get(`/api/meets/${this.meetId}/award-settings`);

                // Initialize with default settings if null
                this.awardSettings = {
                    awardRule: data.settings?.award_rule || 'top3',
                    customAwardCount: data.settings?.custom_award_count || 3,
                };

                // Set up category overrides
                if (data.overrides && data.overrides.length) {
                    data.overrides.forEach((override) => {
                        this.categoryOverrides[override.result_category_id] = {
                            awardRule: override.award_rule,
                            customAwardCount: override.custom_award_count,
                        };
                    });
                }
            } catch (error) {
                console.error('Error loading award settings:', error);
                this.snackbar = {
                    show: true,
                    text: 'Could not load award settings',
                    color: 'error',
                };
            } finally {
                this.loadingSettings = false;
            }
        },

        getAwardCount(category) {
            const totalAthletes = category.results?.length || 0;

            if (totalAthletes === 0) return 0;

            // Check if there's an override for this category
            let rule;
            let customCount;

            if (this.categoryOverrides[category.id]) {
                rule = this.categoryOverrides[category.id].awardRule;
                customCount = this.categoryOverrides[category.id].customAwardCount;
            } else {
                rule = this.awardSettings.awardRule;
                customCount = this.awardSettings.customAwardCount;
            }

            return this.calculateAwardCount(rule, totalAthletes, customCount);
        },

        calculateAwardCount(rule, totalAthletes, customCount = null) {
            if (totalAthletes === 0) return 0;

            switch (rule) {
                case 'top3':
                    return Math.min(3, totalAthletes);
                case 'top50plus1':
                    return Math.min(Math.ceil(totalAthletes / 2) + 1, totalAthletes);
                case 'top6':
                    return Math.min(6, totalAthletes);
                case 'top8':
                    return Math.min(8, totalAthletes);
                case 'top10':
                    return Math.min(10, totalAthletes);
                case 'all':
                    return totalAthletes;
                case 'custom':
                    return Math.min(customCount || 3, totalAthletes);
                default:
                    return Math.min(3, totalAthletes);
            }
        },

        showConfetti() {
            // We would typically use a library like canvas-confetti here
            // For now, we'll use a simple animation with CSS
            this.snackbar = {
                show: true,
                color: 'success',
                text: '🎉 Congratulations! All awards have been distributed! 🏆',
            };

            // Animate the celebration icon
            const celebrationIcons = document.querySelectorAll('.celebration-icon');
            celebrationIcons.forEach((icon) => {
                icon.classList.add('celebration-animation');
                setTimeout(() => {
                    icon.classList.remove('celebration-animation');
                }, 2000);
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.awards-container {
    max-width: 100%;
}

.awards-tabs {
    .v-tab {
        height: 56px;
        font-size: 1.1rem;
    }
}

.awards-tabs-content {
    padding-top: 16px;
}

.session-select {
    ::v-deep .v-input__slot {
        min-height: 56px;
    }
}

.session-checkbox {
    ::v-deep .v-input--selection-controls__ripple {
        transform: scale(1.5);
    }

    ::v-deep .v-input--selection-controls__input {
        transform: scale(1.25);
        margin-right: 12px;
    }
}

.ceremony-progress-card {
    border-left: 5px solid;
    transition: all 0.3s ease;

    &.success--text {
        border-left-color: var(--v-success-base);
    }

    &.light-green--text {
        border-left-color: var(--v-light-green-base);
    }

    &.amber--text {
        border-left-color: var(--v-amber-base);
    }

    &.orange--text {
        border-left-color: var(--v-orange-base);
    }

    &.blue-grey--text {
        border-left-color: var(--v-blue-grey-base);
    }
}

.celebration-container {
    overflow: hidden;
    position: relative;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 1px dashed #4caf50;
    transition: all 0.5s ease;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(45deg, rgba(76, 175, 80, 0.1), rgba(255, 235, 59, 0.1), rgba(33, 150, 243, 0.1));
        z-index: 0;
    }

    & > * {
        position: relative;
        z-index: 1;
    }
}

.celebration-icon {
    font-size: 48px;
    transition: transform 0.5s ease;

    &.celebration-animation {
        animation: celebrateIcon 2s ease;
    }
}

.completed-panel {
    position: relative;

    ::v-deep .v-expansion-panel-header {
        background-color: rgba(76, 175, 80, 0.05);
        transition: background-color 0.3s ease;
    }
}

/* Animation for completed division indicator */
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: scale(0.5);
    }
    to {
        opacity: 0.85;
        transform: scale(1);
    }
}

/* Animation for celebration icon */
@keyframes celebrateIcon {
    0% {
        transform: scale(1);
    }
    25% {
        transform: scale(1.5) rotate(10deg);
    }
    50% {
        transform: scale(1.2) rotate(-10deg);
    }
    75% {
        transform: scale(1.5) rotate(10deg);
    }
    100% {
        transform: scale(1);
    }
}

.confetti-container {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 9999;
}

/* Optimization for iPad landscape mode */
@media only screen and (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    .awards-container {
        padding: 16px !important;
    }
}

/* Optimization for iPad portrait mode */
@media only screen and (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    .awards-container {
        padding: 24px !important;
    }

    .awards-tabs .v-tab {
        min-width: 120px;
    }
}

.celebration-card {
    border: 1px dashed rgba(76, 175, 80, 0.5);
    background-color: rgba(76, 175, 80, 0.03);
    border-radius: 8px;
}

.progress-summary {
    background-color: rgba(25, 118, 210, 0.02);
}

/* Remove or simplify excessive animations */
</style>
